import { useState, useCallback, useEffect, useRef } from 'preact/hooks'
import { LogOut, Loader2 } from 'lucide-preact'
import { googleAuthApi } from '@/api/googleAuth'
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon'
import '@googleworkspace/drive-picker-element'
import type { PickerPickedEvent } from '@googleworkspace/drive-picker-element'

export interface GoogleDriveFile {
    fileId: string
    fileName: string
    mimeType: string
    /** Undefined when the Picker did not report a size — never coerce that to 0. */
    sizeBytes?: number
}

/**
 * The subset of the Picker's document payload this component reads.
 *
 * `@types/google.picker` is only a devDependency of the picker element package, so it is not
 * installed here and `google.picker.ResponseObject` silently resolves to `any` under skipLibCheck.
 * Declaring what we actually touch is narrower and more honest than reading through that `any`.
 */
interface PickedDriveDocument {
    id: string
    name: string
    mimeType: string
    /** The Picker sends this as a string in most responses, and omits it for some item kinds. */
    sizeBytes?: string | number
}

/** Coerces the Picker's stringly-typed size, yielding undefined rather than NaN or a bogus 0. */
function readSizeBytes(doc: PickedDriveDocument): number | undefined {
    const raw = doc.sizeBytes
    if (raw === undefined || raw === null || raw === '') return undefined
    const parsed = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

export interface GoogleDriveAuthState {
    isConnected: boolean
    providerEmail: string
    isLoading: boolean
    isConnecting: boolean
    connect: () => Promise<void>
    disconnect: () => Promise<void>
}

interface GoogleDrivePickerProps {
    auth: GoogleDriveAuthState
    onFilePicked: (file: GoogleDriveFile) => void
    onError?: (error: string | undefined) => void
}

// Google Workspace MIME types that are native and can't be downloaded directly
const GOOGLE_NATIVE_MIME_TYPES = [
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation',
    'application/vnd.google-apps.drawing',
    'application/vnd.google-apps.form',
    'application/vnd.google-apps.site',
    'application/vnd.google-apps.script',
]

interface PickerAuth {
    accessToken: string
    pickerApiKey: string
}

export function GoogleDrivePicker({ auth, onFilePicked, onError }: GoogleDrivePickerProps) {
    const { isConnected, providerEmail, isLoading, isConnecting, connect, disconnect } = auth
    const [isPicking, setIsPicking] = useState(false)
    const [pickedFile, setPickedFile] = useState<GoogleDriveFile | null>(null)
    const [pickerAuth, setPickerAuth] = useState<PickerAuth | null>(null)
    const pickerRef = useRef<HTMLElement>(null)

    // Attach event listeners to the drive-picker element
    useEffect(() => {
        const el = pickerRef.current
        if (!el) return

        const handlePicked = (e: PickerPickedEvent) => {
            const doc: PickedDriveDocument = e.detail.docs[0]

            // Check if it's a Google Workspace native file
            if (GOOGLE_NATIVE_MIME_TYPES.includes(doc.mimeType)) {
                onError?.(`Google Workspace files (${doc.mimeType.split('.').pop()}) are not yet supported. Please select a regular file (PDF, image, Office document, etc.).`)
                setPickerAuth(null)
                setIsPicking(false)
                return
            }

            const file: GoogleDriveFile = {
                fileId: doc.id,
                fileName: doc.name,
                mimeType: doc.mimeType,
                sizeBytes: readSizeBytes(doc),
            }
            setPickedFile(file)
            onFilePicked(file)
            setPickerAuth(null)
            setIsPicking(false)
        }

        const handleCanceled = () => {
            setPickerAuth(null)
            setIsPicking(false)
        }

        el.addEventListener('picker-picked', handlePicked)
        el.addEventListener('picker-canceled', handleCanceled)
        return () => {
            el.removeEventListener('picker-picked', handlePicked)
            el.removeEventListener('picker-canceled', handleCanceled)
        }
    }, [pickerAuth, onFilePicked, onError])

    const openPicker = useCallback(async () => {
        setIsPicking(true)
        try {
            const tokenResponse = await googleAuthApi.getPickerToken()
            if (!tokenResponse.data) {
                onError?.('Failed to get Google Drive access. Please reconnect your account.')
                await disconnect()
                setIsPicking(false)
                return
            }

            setPickerAuth({
                accessToken: tokenResponse.data.accessToken,
                pickerApiKey: tokenResponse.data.pickerApiKey,
            })
        } catch (error) {
            console.error('Failed to open Google Picker:', error)
            onError?.('Failed to open Google Drive file picker. Please try again.')
            setIsPicking(false)
        }
    }, [onError, disconnect])

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking Google Drive connection...
            </div>
        )
    }

    if (!isConnected) {
        return (
            <div className="flex justify-center py-6">
                <button
                    type="button"
                    onClick={() => { onError?.(undefined); connect() }}
                    disabled={isConnecting}
                    className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50"
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Connecting...
                        </>
                    ) : (
                        <>
                            <GoogleDriveIcon className="w-5 h-5" />
                            Sign in with Google Drive
                        </>
                    )}
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {/* Connected status */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                    Connected as <span className="font-medium text-slate-700 dark:text-slate-200">{providerEmail}</span>
                </span>
                <button
                    type="button"
                    onClick={disconnect}
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                    <LogOut className="w-3 h-3" />
                    Disconnect
                </button>
            </div>

            {/* Drive picker web component (hidden, renders no visible UI) */}
            {pickerAuth && (
                <drive-picker
                    ref={pickerRef}
                    oauth-token={pickerAuth.accessToken}
                    developer-key={pickerAuth.pickerApiKey}
                    title="Select a file from Google Drive"
                >
                    <drive-picker-docs-view
                        include-folders="false"
                        select-folder-enabled="false"
                    />
                    <drive-picker-docs-view
                        enable-drives="true"
                    />
                </drive-picker>
            )}

            {/* Pick file button or picked file display */}
            {pickedFile ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20">
                    <GoogleDriveIcon className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                            {pickedFile.fileName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {pickedFile.mimeType}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setPickedFile(null)
                            openPicker()
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                    >
                        Change
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={openPicker}
                    disabled={isPicking}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 bg-slate-50 dark:bg-slate-700/30 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50"
                >
                    {isPicking ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Opening Google Drive...
                        </>
                    ) : (
                        <>
                            <GoogleDriveIcon className="w-5 h-5" />
                            Pick from Google Drive
                        </>
                    )}
                </button>
            )}
        </div>
    )
}
